import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Litepicker from '@/components/Base/Litepicker';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import Chart from '@/components/Base/Chart';
import { fetchSurveyTracking, type SurveyTrackingData } from '@/api/surveyTracking';

const TRACKING_DATE_RANGE_KEY = 'ldsurvey:admin:surveyTrackingDateRange';

function todayMYT() {
  return dayjs().format('YYYY-MM-DD');
}

function todayISORange() {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    startISO: dayjs(today).startOf('day').toISOString(),
    endISO: dayjs(today).endOf('day').toISOString(),
  };
}

function toIsoRange(range: string) {
  const raw = (range || '').trim();
  if (!raw) return null;
  const [startRaw, endRaw] = raw.split(' - ');
  const startStr = (startRaw || '').trim();
  const endStr = ((endRaw || '') || startStr).trim();
  if (!startStr) return null;
  const start = dayjs(startStr);
  const end = dayjs(endStr);
  if (!start.isValid() || !end.isValid()) return null;
  return {
    startISO: start.startOf('day').toISOString(),
    endISO: end.endOf('day').toISOString(),
  };
}

export default function Surveys() {
  const readInitialDateRange = () => {
    if (typeof window === 'undefined') return `${todayMYT()} - ${todayMYT()}`;
    const saved = window.localStorage.getItem(TRACKING_DATE_RANGE_KEY);
    return saved?.trim() || `${todayMYT()} - ${todayMYT()}`;
  };

  const [dateRange, setDateRange] = useState(readInitialDateRange);
  const [confirmedRange, setConfirmedRange] = useState(
    () => toIsoRange(readInitialDateRange()) ?? todayISORange()
  );
  const [data, setData] = useState<SurveyTrackingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const value = dateRange.trim();
    if (value) window.localStorage.setItem(TRACKING_DATE_RANGE_KEY, value);
  }, [dateRange]);

  const loadData = useCallback(async () => {
    if (!confirmedRange) return;
    setLoading(true);
    setError('');
    try {
      const result = await fetchSurveyTracking({
        startISO: confirmedRange.startISO,
        endISO: confirmedRange.endISO,
      });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load survey tracking');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [confirmedRange]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSearch = () => {
    const iso = toIsoRange(dateRange);
    if (!iso) {
      setError('Please select a valid date range');
      return;
    }
    setConfirmedRange(iso);
  };

  const funnelChart = useMemo(() => {
    const stages = data?.funnel ?? [];
    return {
      type: 'bar' as const,
      data: {
        labels: stages.map((stage) => stage.label),
        datasets: [
          {
            label: 'Sessions',
            data: stages.map((stage) => stage.count),
            backgroundColor: '#93c5fd',
            borderColor: '#60a5fa',
            borderWidth: 1,
            borderRadius: 5,
            borderSkipped: false,
            barThickness: 22,
            maxBarThickness: 28,
          },
        ],
      },
      options: {
        indexAxis: 'y' as const,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: {
            display: true,
            anchor: 'end' as const,
            align: 'end' as const,
            offset: 4,
            color: '#1e293b',
            font: { size: 9, weight: 600 as const },
            formatter: (_value: number, ctx: { dataIndex: number }) => {
              const stage = stages[ctx.dataIndex];
              if (!stage || stage.count <= 0) return '';
              const parts = [stage.count.toLocaleString()];
              if (stage.pctOfPrevious != null && ctx.dataIndex > 0) {
                parts.push(`${stage.pctOfPrevious}%`);
              }
              return parts.join(' · ');
            },
          },
          tooltip: {
            callbacks: {
              label: (ctx: { dataIndex: number }) => {
                const stage = stages[ctx.dataIndex];
                if (!stage) return '';
                const lines = [` Count: ${stage.count.toLocaleString()}`];
                lines.push(` ${stage.pctOfTotal}% of all sessions`);
                if (stage.pctOfPrevious != null && ctx.dataIndex > 0) {
                  lines.push(` ${stage.pctOfPrevious}% of previous stage`);
                }
                return lines;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            grace: '15%',
            grid: { color: 'rgba(148,163,184,0.25)' },
            ticks: { font: { size: 10 } },
          },
          y: {
            grid: { display: false },
            ticks: { font: { size: 10 } },
          },
        },
      },
    };
  }, [data?.funnel]);

  const summary = data?.summary;

  return (
    <div className="p-6 space-y-5">
      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
              Survey Tracking
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Track survey session progress and completion funnel
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:min-w-[280px]">
              <Lucide
                icon="Calendar"
                className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <Litepicker
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                options={{
                  format: 'YYYY-MM-DD',
                  singleMode: false,
                  numberOfColumns: 2,
                  numberOfMonths: 2,
                  autoApply: false,
                }}
                className="w-full rounded-lg border border-slate-200/80 bg-white py-2.5 pl-10 pr-4 text-sm dark:border-darkmode-400 dark:bg-darkmode-600"
              />
            </div>
            <Button type="button" variant="primary" onClick={handleSearch} disabled={loading}>
              {loading ? (
                <>
                  <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
                  Loading…
                </>
              ) : (
                <>
                  <Lucide icon="Search" className="stroke-[1.3] w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {[
            { label: 'Total Sessions', value: summary.totalSessions },
            { label: 'Completed', value: summary.completed },
            { label: 'Abandoned', value: summary.abandoned },
            { label: 'TnG Sent', value: summary.tngSent },
            { label: 'Completion Rate', value: `${summary.completionRatePct}%` },
          ].map((item) => (
            <div
              key={item.label}
              className="box rounded-xl border border-slate-200/60 px-4 py-3 shadow-sm dark:border-darkmode-400"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {item.label}
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-800 dark:text-slate-100">
                {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 p-3 h-[360px] shadow-sm flex flex-col">
        <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
          Completion Funnel
        </p>
        <div className="flex-1 min-h-0">
          {loading && !data ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">
              <Lucide icon="Loader" className="w-5 h-5 mr-2 animate-spin" />
              Loading funnel…
            </div>
          ) : (data?.funnel.length ?? 0) === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">
              No sessions in the selected date range
            </div>
          ) : (
            <Chart {...funnelChart} height="auto" />
          )}
        </div>
      </div>
    </div>
  );
}
